import { InteractionMode } from "@/registration-tools/common/interaction/single-pin-interaction";
import { GUID } from "@faro-lotv/foundation";
import { ISOTimeString } from "@faro-lotv/ielement-types";
import {
  EntityTransformOverrides,
  RevisionTransformCache,
} from "../revision/revision-transform-cache";

/** The edit mode, defining which actions the user can take while editing. */
export enum RegistrationEditMode {
  /** Moving and rotating scans. */
  editScans = "editScans",
  /** Manually adding registration edges, which will _always_ be included in the cloud registration. */
  addConnection = "addConnection",
  /** Manually delete registration edges, which will _never_ be included in the cloud registration. */
  deleteConnection = "deleteConnection",
}

/** The edit mode that's enabled when entering edit mode initially. */
export const DEFAULT_EDIT_MODE = RegistrationEditMode.editScans;

export enum UserEditType {
  /** Manually adding registration edges, which will _always_ be included in the cloud registration. */
  addConnection = "addConnection",
  /** Manually delete registration edges, which will _never_ be included in the cloud registration. */
  deleteConnection = "deleteConnection",
  /** Moving scans. */
  moveScans = "moveScans",
}

/** A connection added manually be the user. */
export type UserEditAddConnection = {
  type: UserEditType.addConnection;
  /** ID of the edit. Can later be used to persist the connection. */
  id: GUID;
  /** The ID of the source scan of the connection. */
  sourceId: GUID;
  /** The ID of the target scan of the connection. */
  targetId: GUID;
  /** The ID of the user who created the connection. */
  createdBy: GUID;
  /** The ISO timestamp when the edit was performed. */
  createdAt: ISOTimeString;
};

/** A connection deleted manually by the user. */
export type UserEditDeleteConnection = {
  type: UserEditType.deleteConnection;
  /** ID of the edit. */
  id: GUID;
  /** The ID of the source scan of the connection. */
  sourceId: GUID;
  /** The ID of the target scan of the connection. */
  targetId: GUID;
  /** The ID of the user who deleted the connection. */
  createdBy: GUID;
  /** The ISO timestamp when the edit was performed. */
  createdAt: ISOTimeString;
};

export type UserEditTransformScans = {
  type: UserEditType.moveScans;
  /** The type of movement performed by the user. */
  mode: InteractionMode;
  /** ID of the edit. */
  id: GUID;
  /** The IDs of the scans to move. */
  scanIds: GUID[];
  /** The new local poses of the moved entities, mapped by scan ID. */
  newPoses: EntityTransformOverrides;
  /** The new global transforms of the entities and their descendants. */
  cachedWorldTransforms: RevisionTransformCache;
  /** The ISO timestamp when the edit was performed. */
  createdAt: ISOTimeString;
};

/** A connection added or deleted manually by the user. */
export type ConnectionEdit = UserEditDeleteConnection | UserEditAddConnection;

/**
 * Type guard to check if a UserEdit is a connection edit (addConnection or deleteConnection).
 *
 * @param edit The UserEdit to check.
 * @returns True if the edit is a connection edit, false otherwise.
 */
export function isConnectionEdit(edit: UserEdit): edit is ConnectionEdit {
  return (
    edit.type === UserEditType.addConnection ||
    edit.type === UserEditType.deleteConnection
  );
}

/** An edit performed by the user. */
export type UserEdit = ConnectionEdit | UserEditTransformScans;
